import React, {Component} from 'react'
import propTypes from 'prop-types'
import {connect} from 'react-redux'
import {removeCompanyImage, uploadCompanyImage} from 'actions/contacts'
import {deleteLogo, uploadLogo} from 'actions/settings'
import {prefix} from 'helpers/component'
import {COMPANY_TYPE, getCompanyName} from 'helpers/contacts'
import iconNames from 'theme/icons'
import Icon from 'components/icon'
import FileSelect from 'components/fileSelect'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import {get, isNil} from 'lodash'
import themeVars from 'theme/themeVars'
import './companyImage.scss'
import {t} from "helpers/language"

const getClassName = prefix('companyImage-')

class CompanyImage extends Component {
  constructor(props) {
    super(props)
    this.state = {
      imageLoaded: false,
      menuOpen: false,
      menuAnchor: null,
    }
  }

  componentDidUpdate = (prevProps) => {
    if (this.props.lastUpdated !== prevProps.lastUpdated)
      this.setState({imageLoaded: false})
  }

  onImageLoaded = () => {
    this.setState({imageLoaded: true})
  }

  showMenu = (e) => {
    this.setState({
      menuOpen: true,
      menuAnchor: e.currentTarget,
    })
  }

  hideMenu = () => {
    this.setState({menuOpen: false})
  }

  fileSelected = (file) => {
    this.hideMenu()
    this.props.onUpload(file)
  }

  doRemove = () => {
    this.hideMenu()
    this.props.onRemove()
  }

  getIconName = () => {
    const {companyType} = this.props
    return isHomeCompany(companyType)
        ? iconNames.homeCompany
        : iconNames.company
  }

  getSourceUrl = () => {
    const {company, lastUpdated} = this.props
    const url = get(company, 'companyLogoImage.url', null)
    return `${url}?ms=${lastUpdated}`
  }

  getImage = () => {
    const {company, size} = this.props
    return (
        <img
            className={getClassName('image')}
            src={this.getSourceUrl()}
            onLoad={this.onImageLoaded}
            style={{
              maxWidth: size,
              maxHeight: size,
            }}
            alt={getCompanyName(company)}
        />
    )
  }

  getOverlay = () => {
    const {allowChange} = this.props

    if (!allowChange)
      return null

    return (
        <div className={getClassName('overlay')}>
          <Icon
              name={iconNames.edit}
              size={28}
              color={themeVars.palette.primaryIconColor}/>
        </div>
    )
  }

  getUploadLabel = () => {
    const {companyType} = this.props
    const {imageLoaded} = this.state

    if (imageLoaded)
      return isHomeCompany(companyType)
          ? t('Change your company logo')
          : t('Change this company logo')

    return isHomeCompany(companyType)
        ? t('Add your company logo')
        : t('Add a company logo')
  }

  getRemoveLabel = () => {
    const {companyType} = this.props
    return isHomeCompany(companyType)
        ? t('Remove your company logo')
        : t('Remove this company logo')
  }

  getUploadMenuItem = () => {
    return (
        <FileSelect key="upload" onSelect={this.fileSelected}>
          <MenuItem
            key={'upload-company-image'}
            className={'upload-company-image'}
          >
            <ListItemIcon><Icon name={iconNames.upload}/></ListItemIcon>
            {this.getUploadLabel()}
          </MenuItem>
        </FileSelect>
    )
  }

  getRemoveMenuItem = () => {
    return (
        <MenuItem
          key={'remove-company-image'}
          className={'remove-company-image'}
          onClick={this.doRemove}
          style={{color: themeVars.palette.errorColor}}
        >
          <ListItemIcon><Icon name={iconNames.delete} color={themeVars.palette.errorColor}/></ListItemIcon>
          {this.getRemoveLabel()}
        </MenuItem>
    )
  }

  getMenuOptions = () => {
    const {imageLoaded} = this.state
    return imageLoaded
        ? [this.getUploadMenuItem(), this.getRemoveMenuItem()]
        : [this.getUploadMenuItem()]
  }

  getContainerStyle = () => {
    const {size, padding} = this.props

    return {
      width: size,
      height: size,
      padding,
      display: this.isDisplayed() ? 'block' : 'none',
    }
  }

  getMenu = () => {
    const {menuOpen, menuAnchor} = this.state
    return (
        <Menu
          classes={{paper: 'company-image-popover'}}
          getContentAnchorEl={null}
          anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
          transformOrigin={{horizontal: 'left', vertical: 'top'}}
          open={menuOpen}
          anchorEl={menuAnchor}
          onClick={(e) => e.stopPropagation()}
          onClose={this.hideMenu}
          MenuListProps={{className: 'company-image-menu'}}
          disableRestoreFocus={true}>
            {this.getMenuOptions()}
          </Menu>
    )
  }

  getContainerClasses = () => {
    const {allowChange} = this.props
    const {imageLoaded, menuOpen} = this.state

    return [
      'container',
      imageLoaded ? 'withImage' : null,
      allowChange ? 'editable' : null,
      menuOpen ? 'menuOpen' : null,
    ].filter(clazz => !isNil(clazz))
  }

  onClick = (e) => {
    const {allowChange} = this.props
    if (allowChange)
      this.showMenu(e)
  }

  getIcon = () => {
    const {showIcon, size, iconColor} = this.props
    const {imageLoaded} = this.state

    if (!showIcon)
      return null

    if (imageLoaded)
      return null

    const color = iconColor || themeVars.palette.primaryIconColor

    return (
        <Icon
            className={getClassName('icon')}
            name={this.getIconName()}
            size={size}
            color={color}/>
    )
  }

  isDisplayed = () => {
    const {showIcon} = this.props
    const {imageLoaded} = this.state
    return showIcon || imageLoaded
  }

  render() {
    return (
        <div
            className={getClassName(this.getContainerClasses())}
            style={this.getContainerStyle()}
            onClick={this.onClick}>

          {this.getImage()}
          {this.getIcon()}
          {this.getOverlay()}
          {this.getMenu()}
        </div>
    )
  }
}

CompanyImage.propTypes = {
  company: propTypes.object.isRequired,
  companyType: propTypes.number.isRequired,
  size: propTypes.number.isRequired,
  showIcon: propTypes.bool,
  iconColor: propTypes.string,
  padding: propTypes.number,
  allowChange: propTypes.bool,
  lastUpdated: propTypes.number.isRequired,
  onUpload: propTypes.func,
  onRemove: propTypes.func,
}

CompanyImage.defaultProps = {
  padding: 0,
  showIcon: true,
  iconColor: null,
  allowChange: false,
}

const isHomeCompany = (companyType) => (companyType === COMPANY_TYPE.home)

const mapStateToProps = (state, ownProps) => {
  const companyId = get(ownProps, 'company.id', null)
  const imageDates = state.contactsContext.companyImageDates
  return {
    lastUpdated: isHomeCompany(ownProps.companyType)
        ? state.auth.logoUpdated || imageDates.default
        : imageDates[companyId] || imageDates.default,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const companyId = get(ownProps, 'company.id', null)
  return {
    onUpload: (file) => {
      if (isHomeCompany(ownProps.companyType)) {
        dispatch(uploadLogo(file))
      } else {
        dispatch(uploadCompanyImage(companyId, file))
      }
    },
    onRemove: () => {
      if (isHomeCompany(ownProps.companyType)) {
        dispatch(deleteLogo())
      } else {
        dispatch(removeCompanyImage(companyId))
      }
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CompanyImage)
